import CameraIcon from '@assets/drawer-icon/CameraIcon';
import CameraOverview from '@assets/drawer-icon/CameraOverview';
import DashboardIcon from '@assets/drawer-icon/DashboardIcon';
import ExemptVrnsIcon from '@assets/drawer-icon/ExemptVrnsIcon';
import LocationsIcon from '@assets/drawer-icon/LocationsIcon';
import MapOverviewIcon from '@assets/drawer-icon/MapOverviewIcon';
import TaskManagement from '@assets/drawer-icon/TaskManagement';
import TrackIcon from '@assets/drawer-icon/TrackIcon';
import V2DashboardIcon from '@assets/drawer-icon/V2DashboardIcon';
import VerificationIcon from '@assets/drawer-icon/VerificationIcon';
import ZoneDashboardIcon from '@assets/drawer-icon/ZoneDashboardIcon';
import useMedia from '@hooks/useMedia';
import { VerificationQueueMode } from '@pages/verification/hooks/VerificationProvider';
import React, { useEffect, useState } from 'react';
import { TbChartBubble, TbSettingsCheck } from 'react-icons/tb';
import { IoAnalyticsOutline, IoNotificationsOutline } from 'react-icons/io5';
import { PiSimCardDuotone } from 'react-icons/pi';
import { MdOutlineAssignmentTurnedIn } from 'react-icons/md';
import i18n from 'src/translations/config';

export interface DrawerItem {
    id: DrawerPage;
    label: string;
    icon: JSX.Element;
    path?: any;
    children?: DrawerItem[];
}

export enum DrawerPage {
    Cameras,
    ZoneManagement,
    MapOverview,
    ZoneDashboard,
    Verification,
    OverviewVerification,
    OverviewCameras,
    Dashboard,
    Report,
    ReportHeatMap,
    CameraTime,
    ReportsAnalyzing,
    TaskManagement,
    ExemptVRNsManagement,
    ConfigurationTemplate,
    ParkingOverview,
    Alert,
    SimManagement,
    CheckPhoto,
    EcamV2Tracks,
    EcamV2Dashboard,
    EcamV2Review,
    EcamV2PreProcess,
    EcamV2TracksDashboard,
}

export default function useDrawer() {
    const { t } = i18n;
    const [open, setOpen] = useState(false);
    const [activeItem, setActiveItem] = useState<DrawerPage>(DrawerPage.Cameras);
    const [isShowDrawer, setIsShowDrawer] = useState<boolean>(false);
    const [openDrawerSmall, setOpenDrawerSmall] = useState(false);
    const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
    const { isMobileSM, isMediumMD } = useMedia();

    const handleDrawerClose = () => {
        if (isMediumMD) {
            setOpenDrawerSmall(true);
            setIsShowDrawer(true);
        } else {
            setOpen(!open);
        }
    };

    const handleDrawerOpen = () => {
        if (isMediumMD) {
            setOpenDrawerSmall(true);
            setIsShowDrawer(false);
        } else {
            setOpen(!open);
        }
    };

    const changeIsShowDrawer = () => {
        setIsShowDrawer(!isShowDrawer);
    };

    const toggleGroup = (id: DrawerPage) => {
        setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    let ListDrawItem: DrawerItem[] = [
        {
            id: DrawerPage.Cameras,
            label: t('drawer.cameras'),
            icon: <CameraIcon />,
            path: '/cameras',
        },
        {
            id: DrawerPage.ZoneManagement,
            label: t('drawer.zoneManagement'),
            icon: <LocationsIcon />,
            path: 'zone-management',
        },
        {
            id: DrawerPage.MapOverview,
            label: t('drawer.mapOverview'),
            icon: <MapOverviewIcon />,
            path: 'map-overview',
        },
        {
            id: DrawerPage.ZoneDashboard,
            label: t('drawer.zoneDashboard'),
            icon: <ZoneDashboardIcon />,
            path: 'zone-dashboard',
        },
        {
            id: DrawerPage.Report,
            label: 'Insights',
            icon: <IoAnalyticsOutline fontSize={28} />,
            path: '/insights',
        },
        {
            id: DrawerPage.ReportHeatMap,
            label: 'PCNs chart',
            icon: <TbChartBubble fontSize={28} />,
            path: '/pcns-chart',
        },
        {
            id: DrawerPage.Verification,
            label: t('drawer.verification'),
            icon: <VerificationIcon />,
            path: VerificationQueueMode.Verification,
        },
        {
            id: DrawerPage.OverviewVerification,
            label: t('drawer.VerificationsOverview'),
            icon: <DashboardIcon />,
            path: '/overview-verification',
        },
        {
            id: DrawerPage.OverviewCameras,
            label: t('drawer.CameraOverview'),
            icon: <CameraOverview />,
            path: '/camera-overview',
        },
        // {
        //     id: DrawerPage.ReportsAnalyzing,
        //     label: t('drawer.ReportsAnalyzing'),
        //     icon: <TbChartHistogram fontSize={26} stroke={color.grey600} className="icon-svg-stroke" />,
        //     path: '/reports-analysing',
        // },
        {
            id: DrawerPage.TaskManagement,
            label: t('drawer.TaskManagement'),
            icon: <TaskManagement />,
            path: '/task-management',
        },
        {
            id: DrawerPage.Alert,
            label: t('drawer.Alert'),
            icon: <IoNotificationsOutline fontSize={22} />,
            path: '/alert',
        },
        {
            id: DrawerPage.ExemptVRNsManagement,
            label: t('drawer.ExemptionListManagement'),
            icon: <ExemptVrnsIcon />,
            path: '/exemption-list',
        },
        {
            id: DrawerPage.ConfigurationTemplate,
            label: t('drawer.ConfigurationTemplate'),
            icon: <TbSettingsCheck fontSize={26} />,
            path: '/configuration-template',
        },
        {
            id: DrawerPage.SimManagement,
            label: t('drawer.SimManagement'),
            icon: <PiSimCardDuotone fontSize={26} />,
            path: '/sim-management',
        },
        {
            id: DrawerPage.EcamV2PreProcess,
            label: 'Pre-process',
            icon: <MdOutlineAssignmentTurnedIn fontSize={26} />,
            children: [
                { id: DrawerPage.EcamV2Tracks, label: 'Vehicle Track', icon: <TrackIcon />, path: '/vehicle-track' },
                {
                    id: DrawerPage.EcamV2Review,
                    label: 'Pre-process queue',
                    icon: <MdOutlineAssignmentTurnedIn fontSize={26} />,
                    path: '/pre-process-queue',
                },
                {
                    id: DrawerPage.EcamV2Dashboard,
                    label: 'Funnel dashboard',
                    icon: <V2DashboardIcon />,
                    path: '/funnel-dashboard',
                },
                {
                    id: DrawerPage.EcamV2TracksDashboard,
                    label: 'Pre-process dashboard',
                    icon: <IoAnalyticsOutline fontSize={26} />,
                    path: '/tracks-verification-dashboard',
                },
            ],
        },
        // {
        //     id: DrawerPage.CheckPhoto,
        //     label: 'Check Photo',
        //     icon: <CheckPhotoIcon />,
        //     path: '/check-photo',
        // },
        // {
        //     id: DrawerPage.ParkingOverview,
        //     label: t('drawer.ParkingOverview'),
        //     icon: <CarParkingIcon />,
        //     path: '/car-parking-list',
        // },

        // {
        //     id: DrawerPage.CameraTime,
        //     label: 'Camera Time',
        //     icon: (
        //         <IoIosTimer
        //             style={{
        //                 width: 26,
        //                 height: 26,
        //             }}
        //         />
        //     ),
        //     path: '/cameraTime',
        // },

        // {
        //     id: DrawerPage.Dashboard,
        //     label: t('drawer.dashboard'),
        //     icon: <DashboardIcon />,
        //     path: '/dashboard',
        // },
    ];

    useEffect(() => {
        isMobileSM ? setIsShowDrawer(false) : setIsShowDrawer(true);
    }, [isMobileSM]);

    return {
        ListDrawItem,
        open,
        setOpen,
        handleDrawerClose,
        // logout,
        activeItem,
        setActiveItem,
        isShowDrawer,
        changeIsShowDrawer,
        setIsShowDrawer,
        handleDrawerOpen,
        isMobileSM,
        isMediumMD,
        openDrawerSmall,
        setOpenDrawerSmall,
        openGroups,
        toggleGroup,
    };
}

export const DrawerContext = React.createContext<ReturnType<typeof useDrawer>>({} as any);
